import { useEffect, useState } from 'react';
import { Button, Input } from 'antd';
import { ApiError, updateGuild } from '../../lib/api';
import { useGuildId } from '../../hooks/useGuildId';
import { useAuthStore } from '../../stores/useAuthStore';
import { useServerStore } from '../../stores/useServerStore';
import styles from './ServerSettings.module.css';
import surface from '../ProjectSurface.module.css';
import ProjectPageHeader from '../ProjectPageHeader';

export default function ServerProfilePanel() {
  const guildId = useGuildId();
  const currentUser = useAuthStore((state) => state.currentUser);
  const server = useServerStore((state) => state.guilds.find((guild) => guild.id === guildId));
  const replaceGuild = useServerStore((state) => state.replaceGuild);
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const canUpdate = server?.owner_id === currentUser?.id;

  useEffect(() => {
    setName(server?.name ?? '');
    setIcon(server?.icon ?? '');
    setError('');
  }, [guildId, server?.name, server?.icon]);

  if (!server) return <div className="no-channel" role="status">Loading Project settings…</div>;

  if (!canUpdate) {
    return (
      <section className={`${surface.pageContainer} ${styles.denied}`} aria-label="Project settings access denied">
        <h1 className={surface.pageTitle}>Project Settings</h1>
        <p>You don’t have permission to update this Project.</p>
      </section>
    );
  }

  const save = async () => {
    const targetGuildId = guildId;
    const normalizedName = name.trim();
    const normalizedIcon = icon.trim();
    if (!normalizedName) {
      setError('Project name is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const updated = await updateGuild(targetGuildId, { name: normalizedName, icon: normalizedIcon });
      // A response for a Server left while saving must never overwrite the
      // selected Server's view. The store still reconciles the exact ID.
      replaceGuild(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save Project settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={styles.profile} aria-label="Project profile">
      <ProjectPageHeader title="Settings" />

      <section className={`${surface.pageContainer} ${surface.section} ${styles.section}`} aria-labelledby="project-profile-title">
        <div>
          <h2 className={styles.sectionTitle} id="project-profile-title">General</h2>
          <p className={styles.sectionDescription}>Update the name and icon used for this Project.</p>
        </div>
        <div className={styles.profileSummary}>
          <span className={styles.avatar}>{icon || name[0]?.toUpperCase() || 'S'}</span>
          <div className={styles.summaryCopy}>
            <div className={styles.serverName}>{server.name}</div>
            <div className={styles.description}>Project profile</div>
          </div>
        </div>

        <div className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="server-name">PROJECT NAME</label>
            <Input className={styles.fieldControl} id="server-name" value={name} maxLength={100} onChange={(event) => setName(event.target.value)} disabled={saving} />
            <p className={styles.hint}>This name appears in the Project rail and switcher.</p>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="server-icon">ICON</label>
            <Input className={styles.fieldControl} id="server-icon" value={icon} maxLength={500} onChange={(event) => setIcon(event.target.value)} disabled={saving} placeholder="Emoji or short text" />
          </div>
        </div>
        {error && <p className={styles.error} role="alert">{error}</p>}
        <div className={styles.actions}>
          <Button type="primary" onClick={() => void save()} loading={saving} disabled={name.trim() === server.name && icon.trim() === server.icon}>Save changes</Button>
        </div>
      </section>
    </section>
  );
}
